/** Normalisierung von ISBN, Titeln und Autoren.
 *
 *  Diese Datei ist die einzige Stelle, an der entschieden wird, wann zwei Einträge
 *  "dasselbe Werk" sind. Sie wird beim Import (Dubletten), beim Verknüpfen über
 *  Regalgrenzen ("auch nebenan gelesen") und beim Nachschlagen von Covern benutzt.
 *
 *  Vorsicht, teuer gelernt: Reihenbände unterscheiden sich oft NUR durch eine Zahl
 *  im Titel (Prison Healer 1-3, Red Rising 1-3). Die Zahl darf deshalb nie
 *  wegnormalisiert werden, sonst frisst der Import Bücher.
 */

/** Nur Ziffern und X; Excel-Panzerung (`="978…"`) und Bindestriche fallen weg. */
export function cleanIsbn(value: string | null | undefined): string {
  return (value || "").replace(/[^0-9Xx]/g, "").toUpperCase();
}

export function isValidIsbn(isbn: string): boolean {
  const s = cleanIsbn(isbn);
  return s.length === 10 || s.length === 13;
}

/** ISBN-13 (978-Präfix) → ISBN-10. Amazon-Cover-URLs brauchen genau diese Form. */
export function isbn13to10(isbn13: string): string | null {
  const s = cleanIsbn(isbn13);
  if (s.length !== 13 || !s.startsWith("978")) return null;
  const core = s.slice(3, 12);
  let sum = 0;
  for (let i = 0; i < 9; i++) sum += (10 - i) * Number(core[i]);
  const rest = (11 - (sum % 11)) % 11;
  return core + (rest === 10 ? "X" : String(rest));
}

/** Umlaute und Akzente auf ihre Grundform, damit "Süßkind" und "Suesskind" treffen. */
export function foldAccents(text: string): string {
  return text
    .replace(/ß/g, "ss")
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/Ä/g, "Ae")
    .replace(/Ö/g, "Oe")
    .replace(/Ü/g, "Ue")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

export function tidy(text: string | null | undefined): string {
  return (text || "").replace(/\s+/g, " ").trim();
}

/**
 * Titel für den Vergleich vereinheitlichen: Untertitel nach ":" oder " - " sowie
 * Klammerzusätze ("Roman", "Band 2 der Reihe") entfallen, Zahlen bleiben stehen.
 */
export function normTitle(title: string): string {
  let t = foldAccents(tidy(title).toLowerCase());
  t = t.replace(/\((?:[^)]*)\)/g, " "); // Klammerzusätze
  t = t.split(/\s+[-–—]\s+/)[0]; // "Titel – Untertitel"
  t = t.split(":")[0]; // "Titel: Untertitel"
  t = t.replace(/\b(ein\s+)?roman\b/g, " ");
  t = t.replace(/[^a-z0-9 ]+/g, " ");
  return tidy(t);
}

/** Nachname der ersten genannten Person. "McConaghy, Charlotte" und
 *  "Charlotte McConaghy" ergeben beide "mcconaghy". */
export function authorKey(author: string): string {
  const teile = tidy(author).split(/[,;&]| und /);
  const first = teile[0] || "";
  const parts = tidy(first).split(" ").filter(Boolean);
  if (!parts.length) return "";
  // "Nachname, Vorname" liegt nur vor, wenn VOR dem Komma ein einzelnes Wort
  // steht. Ein Komma allein genügt nicht: Die Metadaten-Suche verbindet mehrere
  // Autoren mit ", " ("Charlotte McConaghy, Jan Schönherr") — dort ist das erste
  // Wort der Vorname und der Nachname steht am Ende des ersten Abschnitts.
  const looksInverted = author.includes(",") && teile.length > 1 && parts.length === 1;
  const name = looksInverted ? parts[0] : parts[parts.length - 1];
  return foldAccents(name.toLowerCase()).replace(/[^a-z]/g, "");
}

/**
 * Schlüssel, über den dasselbe Werk in beiden Regalen erkannt wird — auch dann,
 * wenn es unterschiedliche Ausgaben mit unterschiedlicher ISBN sind. Es wird
 * dadurch NICHTS zusammengeführt; die Bestände bleiben getrennt.
 */
export function linkKey(title: string, author: string): string {
  const t = normTitle(title);
  const a = authorKey(author);
  return t ? `${t}|${a}` : "";
}

/** Grobe Ähnlichkeit zweier Titel (0..1) für die Dubletten-Warnung beim Import. */
export function titleSimilarity(a: string, b: string): number {
  const x = normTitle(a);
  const y = normTitle(b);
  if (!x || !y) return 0;
  if (x === y) return 1;
  const wx = new Set(x.split(" "));
  const wy = new Set(y.split(" "));
  let hit = 0;
  wx.forEach((w) => {
    if (wy.has(w)) hit++;
  });
  return (2 * hit) / (wx.size + wy.size);
}

/** Vorname/Nachname für die Anzeige: "McConaghy, Charlotte" → "Charlotte McConaghy". */
export function displayAuthor(author: string): string {
  const a = tidy(author);
  if (!a.includes(",")) return a;
  const [last, first] = a.split(",");
  if (!first || /\band\b|&/.test(a)) return a;
  return tidy(`${first} ${last}`);
}
